// autopilot-control-directive-shared.ts
//
// Hạ tầng chung cho các lệnh control (approve/pause/resume/answer/redirect/
// revert/abort): kiểu deps + hàm ghi directive vào inbox.
//
// Tách khỏi `autopilot-control-directive-commands.ts` (M1, code-reviewer
// 260916 phase-04): file đó bị P4 đẩy 196 → 252 dòng khi lệnh `answer` nhận
// thêm `--question` (B4) và `approve` nhận `--targets`/`--expires` (E1/H1).
// Tách phần dùng chung ra đây để cả hai file lệnh cùng dưới trần 200 dòng.
//
// KHÔNG đổi hành vi: mọi lệnh vẫn ghi CHỈ vào inbox qua directive-inbox.ts,
// không bao giờ chạm mission.json (spec §3.3 Req 4-7, tests 25-29).
import { writeMachineReadableJson } from "../machine-readable-json-output-contract.js";
import { log } from "../terminal-logger.js";
import { writeDirective } from "./directive-inbox.js";
import { resolveMission } from "./mission-resolver.js";

export interface ControlDirectiveDeps {
  resolveWorkspaceRoot: (repoFlag: string | undefined) => Promise<string>;
  machineLockPath: string;
}

export type ControlDirectiveKind =
  | "approve"
  | "pause"
  | "resume"
  | "answer"
  | "redirect"
  | "revert"
  | "abort"
  | "abort_confirm";

/** [N16] Single source for CLI-local actor identity — shared with `issueAbortChallenge`/`confirmAbortChallenge` so N9's actor comparison never sees a mismatch. */
export function resolveCliActorId(): string {
  return process.env.USER ?? process.env.LOGNAME ?? "unknown";
}

export async function writeControlDirective(
  deps: ControlDirectiveDeps,
  workspaceRoot: string,
  missionFlag: string | undefined,
  kind: ControlDirectiveKind,
  payload: Record<string, unknown>,
  jsonOut: boolean,
): Promise<void> {
  const resolved = await resolveMission({ workspaceRoot, explicitMissionId: missionFlag });
  const receipt = await writeDirective({
    controlRoot: resolved.controlRoot,
    machineLockPath: deps.machineLockPath,
    directive: {
      mission_id: resolved.missionId,
      kind,
      source: "cli_local",
      actor_id: resolveCliActorId(),
      payload,
    },
  });
  if (jsonOut) {
    writeMachineReadableJson({ command: `autopilot ${kind}`, receipt });
  } else {
    log.info(
      `Directive ${receipt.directive_id}: ${receipt.status}${receipt.reason ? ` — ${receipt.reason}` : ""}`,
    );
  }
}
